import { Link } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import BackToTop from "@/components/feature/BackToTop";
import Seo from "@/components/feature/Seo";
import Breadcrumbs from "@/components/feature/Breadcrumbs";
import { gossipVoices } from "@/mocks/gossipList";

const principles = [
  {
    icon: "ri-links-line",
    title: "每条必带脉络",
    text: "不接受只给一张截图就下结论。任何一条进入榜单的事件，都必须能整理出至少三个时间节点。",
  },
  {
    icon: "ri-scales-3-line",
    title: "只整理，不裁定",
    text: "我们呈现双方说法与公开进展，把判断权留给你。网站不会给事件贴上「谁对谁错」的标签。",
  },
  {
    icon: "ri-shield-keyhole-line",
    title: "隐私内容不追",
    text: "涉及个人信息安全、未经同意的私密资料，不整理、不收录、不引导二次传播。",
  },
  {
    icon: "ri-refresh-line",
    title: "状态随时更新",
    text: "已澄清、待回应、持续发酵是会变的。只要出现可核实的新进展，条目状态就会同步调整。",
  },
];

const milestones = [
  { year: "2024", title: "从一份表格开始", text: "最初只是几个朋友把群里刷屏的爆料记进同一张表格，按时间顺序排好。" },
  { year: "2025", title: "固定成 30 条的结构", text: "发现超过 30 条就没人看得完，于是把每期上限定在 30，逼着自己做取舍。" },
  { year: "2026", title: "加上冲突点与状态", text: "读者反馈最需要的是「到底在争什么」，于是每条都补上冲突点与当前状态。" },
];

const contactInfo = [
  { icon: "ri-mail-line", label: "投稿与纠错", value: "tips@guawang30.com" },
  { icon: "ri-customer-service-2-line", label: "内容合作", value: "hello@guawang30.com" },
  { icon: "ri-time-line", label: "人工核对时间", value: "周一至周五 10:00 - 18:00" },
];

export default function About() {
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "AboutPage",
      name: "关于瓜网30",
      url: `${origin}/about`,
      inLanguage: "zh-CN",
      description: "瓜网30的定位、内容原则、发展理念与联系方式。",
      mainEntity: {
        "@type": "Organization",
        name: "瓜网30",
        url: `${origin}/`,
        email: "hello@guawang30.com",
        description: "专注于娱乐事件整理与时间线还原的内容站点。",
      },
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "首页", item: `${origin}/` },
        { "@type": "ListItem", position: 2, name: "关于我们", item: `${origin}/about` },
      ],
    },
  ];

  return (
    <div className="min-h-screen flex flex-col bg-background-50">
      <Seo
        title="关于我们｜瓜网30内容原则与网站定位"
        description="瓜网30是一个把娱乐爆料整理成事件脉络的内容站点，这里说明我们的网站定位、内容原则、发展理念与联系方式，以及每一期30条是怎么筛出来的。"
        keywords="关于瓜网30,瓜网30网站定位,内容原则,娱乐事件整理,事件时间线"
        canonicalPath="/about"
        jsonLd={jsonLd}
      />
      <SiteHeader />

      <main className="flex-1 w-full">
        <section className="w-full bg-background-100 border-b border-background-200">
          <div className="w-full px-4 md:px-8 py-8 md:py-10">
            <Breadcrumbs items={[{ label: "首页", to: "/" }, { label: "关于我们" }]} />
            <div className="mt-5 max-w-3xl">
              <p className="flex items-center gap-2 text-xs text-primary-600 mb-3">
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-plant-line"></i>
                </span>
                ABOUT
              </p>
              <h1 className="font-heading font-black text-2xl md:text-4xl text-foreground-950">
                关于瓜网30
              </h1>
              <p className="mt-4 text-sm md:text-base text-foreground-600 leading-relaxed">
                我们做一件很具体的事：把网上刷屏的娱乐爆料，按事件重新排好顺序。
                不追热点速度，也不写立场鲜明的长评，只负责让你在最短时间里看清楚一件事的来龙去脉。
              </p>
            </div>
          </div>
        </section>

        <section className="w-full px-4 md:px-8 py-12 md:py-14" aria-labelledby="position-title">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-start">
            <div>
              <h2 id="position-title" className="font-heading font-black text-xl md:text-2xl text-foreground-950 mb-4">
                网站定位
              </h2>
              <div className="space-y-4 text-sm md:text-base text-foreground-700 leading-loose">
                <p>
                  市面上不缺爆料，缺的是整理。同一条事件在不同平台常常有完全不同的描述，
                  读者要在互相矛盾的说法里自己拼图，成本很高。瓜网30 想解决的就是这一步。
                </p>
                <p>
                  每期固定收录 30 条，每条包含四样东西：核心人物、冲突点、当前状态、以及一条从最早信号排到最近进展的时间线。
                  看完整条，你至少知道这件事经历了几个阶段、争的是什么、现在停在哪。
                </p>
                <p>
                  我们不生产信息，也不对当事人做道德判断。榜单里所有人物都使用化名，
                  内容来自网友投稿与公开讨论的汇总整理。
                </p>
              </div>

              <div className="mt-8 grid grid-cols-3 gap-4">
                {[
                  { value: "30", label: "每期条数上限" },
                  { value: "8", label: "覆盖主题分类" },
                  { value: "每周", label: "名单更新频率" },
                ].map((s) => (
                  <div key={s.label} className="bg-background-100 border border-background-200 rounded-lg p-4">
                    <p className="font-heading font-black text-xl md:text-2xl text-primary-600">{s.value}</p>
                    <p className="text-xs text-foreground-600 mt-1">{s.label}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="w-full h-64 md:h-[420px] rounded-lg overflow-hidden">
              <img
                src="https://readdy.ai/api/search-image?query=Warm%20editorial%20still%20life%20of%20a%20writing%20desk%20with%20open%20notebooks%2C%20stacked%20newspapers%20and%20a%20brass%20lamp%20in%20amber%20light%2C%20cinematic%20magazine%20aesthetic%2C%20no%20people%2C%20no%20text&width=900&height=1100&seq=gw-about-team&orientation=portrait"
                alt="瓜网30内容整理工作场景 - 暖色桌面与笔记本"
                title="瓜网30 · 关于我们"
                className="w-full h-full object-cover object-top"
              />
            </div>
          </div>
        </section>

        <section className="w-full bg-background-100 border-y border-background-200" aria-labelledby="principle-title">
          <div className="w-full px-4 md:px-8 py-12 md:py-14">
            <h2 id="principle-title" className="font-heading font-black text-xl md:text-2xl text-foreground-950 mb-2">
              内容原则
            </h2>
            <p className="text-sm text-foreground-600 mb-8 max-w-2xl">
              这四条是我们筛掉或保留一条事件的全部依据，也是你能用来判断我们是否可靠的标准。
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-5">
              {principles.map((p, index) => (
                <div
                  key={p.title}
                  className={`flex gap-4 rounded-lg p-5 border ${
                    index === 0 ? "bg-accent-50 border-accent-200" : "bg-background-50 border-background-200"
                  }`}
                >
                  <span className="w-11 h-11 shrink-0 flex items-center justify-center rounded-md bg-secondary-100 text-secondary-700">
                    <i className={`${p.icon} text-xl`}></i>
                  </span>
                  <div>
                    <h3 className="font-heading font-bold text-base text-foreground-950 mb-1.5">{p.title}</h3>
                    <p className="text-sm text-foreground-600 leading-relaxed">{p.text}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="w-full px-4 md:px-8 py-12 md:py-14" aria-labelledby="history-title">
          <h2 id="history-title" className="font-heading font-black text-xl md:text-2xl text-foreground-950 mb-8">
            发展理念
          </h2>
          <ol className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {milestones.map((m) => (
              <li key={m.year} className="relative bg-background-100 border border-background-200 rounded-lg p-5">
                <span className="font-heading font-black text-3xl text-background-400 select-none">{m.year}</span>
                <h3 className="font-heading font-bold text-base text-foreground-950 mt-2 mb-2">{m.title}</h3>
                <p className="text-sm text-foreground-600 leading-relaxed">{m.text}</p>
              </li>
            ))}
          </ol>
        </section>

        <section className="w-full bg-secondary-100 border-y border-secondary-200" aria-labelledby="about-voice-title">
          <div className="w-full px-4 md:px-8 py-12 md:py-14">
            <h2 id="about-voice-title" className="font-heading font-black text-xl md:text-2xl text-foreground-950 mb-8">
              用户口碑
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5">
              {gossipVoices.map((v) => (
                <figure key={v.name} className="bg-background-50 border border-secondary-200 rounded-lg p-5">
                  <blockquote className="text-sm md:text-base text-foreground-800 leading-relaxed">{v.text}</blockquote>
                  <figcaption className="mt-4 flex items-center gap-2">
                    <span className="w-8 h-8 flex items-center justify-center rounded-full bg-primary-100 text-primary-700 font-heading font-bold text-sm">
                      {v.name.slice(0, 1)}
                    </span>
                    <span className="font-heading font-bold text-sm text-foreground-950">{v.name}</span>
                    <span className="text-xs px-2 py-0.5 rounded-full bg-secondary-200 text-secondary-800">{v.tag}</span>
                  </figcaption>
                </figure>
              ))}
            </div>
          </div>
        </section>

        <section className="w-full px-4 md:px-8 py-12 md:py-14" aria-labelledby="about-contact-title">
          <h2 id="about-contact-title" className="font-heading font-black text-xl md:text-2xl text-foreground-950 mb-8">
            联系方式
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 md:gap-5">
            {contactInfo.map((c) => (
              <div key={c.label} className="flex items-start gap-3 bg-background-100 border border-background-200 rounded-lg p-5">
                <span className="w-10 h-10 shrink-0 flex items-center justify-center rounded-md bg-primary-100 text-primary-700">
                  <i className={`${c.icon} text-lg`}></i>
                </span>
                <div>
                  <p className="text-xs text-foreground-500 mb-1">{c.label}</p>
                  <p className="text-sm font-medium text-foreground-950 break-all">{c.value}</p>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-8 flex flex-col sm:flex-row gap-3">
            <Link
              to="/contact"
              className="px-5 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm text-center whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer"
            >
              填写投稿或纠错表单
            </Link>
            <Link
              to="/rankings"
              className="px-5 py-2.5 rounded-md border border-background-300 text-foreground-800 text-sm text-center whitespace-nowrap hover:border-primary-300 hover:text-primary-700 transition-colors cursor-pointer"
            >
              先去看看 30 条大盘点
            </Link>
          </div>
        </section>
      </main>

      <SiteFooter />
      <BackToTop />
    </div>
  );
}